import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * White card on the cool stone canvas: crisp hairline plus the low cool card shadow (A3).
 * Default container for grouped content. Inside `surface-field` it turns tonal blue by itself.
 */
export function Card({
  className,
  interactive,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-lg border border-hairline bg-surface shadow-sm',
        interactive &&
          'transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-px hover:border-hairline-strong hover:shadow-md',
        className,
      )}
      {...rest}
    />
  );
}

/**
 * Section card: uppercase eyebrow + heavy Commissioner title + optional actions, then a hairline
 * and the content. Use for dashboard sections and settings groups.
 */
export function Panel({
  eyebrow,
  title,
  description,
  actions,
  footer,
  children,
  className,
  bodyClassName,
  flush,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Remove body padding (tables, lists). */
  flush?: boolean;
}) {
  const hasHead = eyebrow || title || actions;
  return (
    <section className={cn('rounded-lg border border-hairline bg-surface shadow-sm', className)}>
      {hasHead && (
        <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-5 pb-3.5 pt-4">
          {/* The title keeps a readable width; wide actions wrap below it on phones. */}
          <div className="grid min-w-0 flex-[1_1_12rem] gap-0.5">
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            {title && (
              <h2 className="font-display-tight text-lg leading-tight text-ink">{title}</h2>
            )}
            {description && <p className="text-sm text-ink-muted">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn(hasHead && 'border-t border-hairline', !flush && 'p-5', bodyClassName)}>
        {children}
      </div>
      {footer && (
        <footer className="rounded-b-lg border-t border-hairline bg-canvas/70 px-5 py-3 text-sm">
          {footer}
        </footer>
      )}
    </section>
  );
}

export function Separator({ className, label }: { className?: string; label?: ReactNode }) {
  if (!label) return <hr className={cn('border-0 border-t border-hairline', className)} />;
  return (
    <div
      className={cn('flex items-center gap-3 text-xs text-ink-subtle', className)}
      role="separator"
    >
      <span className="h-px flex-1 bg-hairline" />
      {label}
      <span className="h-px flex-1 bg-hairline" />
    </div>
  );
}
